/**
 * ATL-000381 follow-up — variant-aware enterprise-feature route guard for
 * the `/dispatch-loads/:variant/*` surfaces.
 *
 * The dispatch-loads routes are dynamic: the `:variant` URL slug
 * (`freight | intermodal | livestock | ftl-brokerage`) decides which
 * `mod_*_main` enterprise feature gates the page. This component reads
 * the slug from the route params, resolves it through the canonical
 * `resolveVariantKey` helper (accepts slug OR canonical key, mirroring
 * what the pages themselves tolerate), and delegates to the shared
 * FeatureGateBody:
 *
 *   - unknown / missing variant → /dashboard (same posture the pages
 *     take for a bad slug, but before anything mounts)
 *   - variant's module disabled for the org → /dashboard
 *   - loading → spinner; query error → fail open (see require-feature.tsx)
 *
 * Used as a layout route:
 *   <Route element={<RequireVariantFeature />}>
 *     <Route path="/dispatch-loads/:variant" element={<DispatchLoadsPage />} />
 *     …
 *   </Route>
 */
import { Navigate, useParams } from 'react-router-dom';
import { resolveVariantKey } from '../../../pages/dispatch-loads/dispatch-loads-route';
import { FeatureGateBody } from './require-feature';

export function RequireVariantFeature({ children }: { children?: React.ReactNode }) {
  const { variant } = useParams<{ variant: string }>();
  const featureKey = resolveVariantKey(variant);

  if (!featureKey) {
    return <Navigate to="/dashboard" replace />;
  }

  return <FeatureGateBody featureKey={featureKey}>{children}</FeatureGateBody>;
}

export default RequireVariantFeature;
